import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useMe } from '@/auth/useMe'
import { DesktopOnly } from '@/components/DesktopOnly'
import { ColumnMapping } from '@/features/import/ColumnMapping'
import { FilePicker } from '@/features/import/FilePicker'
import { ImportStepper } from '@/features/import/ImportStepper'
import { PageHeader } from '@/features/preinvest/components/PageHeader'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import { commitImport, downloadImportTemplate, parseImportFile, previewImport } from './api'
import { applyMapping, crmMappingMemory, fieldsFor, mappingProblems } from './fields'
import { ImportHistory } from './ImportHistory'
import { OrganisationsPanel } from './OrganisationsPanel'
import { PreviewGrid } from './PreviewGrid'
import { isUuid } from '../query/validate'
import {
  columnsFor,
  commitRows,
  defaultChoice,
  isOffered,
  mappedFields,
  ORGANISATION_NEW,
  parseOrganisationValue,
} from './rowChoices'
import { emptySettings, type ImportSettings, settingsPayload } from './settings'
import { SettingsStep } from './SettingsStep'
import type {
  CrmField,
  ExistingOrganisation,
  ImportMode,
  NewOrganisation,
  ParsedFile,
  PreviewResult,
  PreviewRow,
  RowChoice,
  RowField,
  RowInput,
} from './types'

type Step = 'file' | 'mapping' | 'preview'
/** The most rows one check may name in `only` (the server's limit). */
const MAX_ONLY = 50
const STEPS: readonly { key: Step | 'done'; label: string }[] = [
  { key: 'file', label: '1 Bestand en instellingen' },
  { key: 'mapping', label: '2 Kolommen' },
  { key: 'preview', label: '3 Controleren' },
  { key: 'done', label: '4 Klaar' },
]
const MODES: readonly { value: ImportMode; label: string; hint: string }[] = [
  { value: 'contacten', label: 'Contacten', hint: 'Personen, met hun organisatie' },
  { value: 'organisaties', label: 'Organisaties', hint: 'Alleen organisaties' },
]

interface Review {
  rows: PreviewRow[]
  choices: RowChoice[]
  newOrganisations: NewOrganisation[]
  existingOrganisations: ExistingOrganisation[]
}

const EMPTY_REVIEW: Review = { rows: [], choices: [], newOrganisations: [], existingOrganisations: [] }

function parseMode(value: string | null): ImportMode {
  return value === 'organisaties' ? 'organisaties' : 'contacten'
}

/**
 * Relaties importeren (spec-63 E6): ① file and settings, ② column mapping, ③ an editable
 * preview checked by the server, then the result page (④). The rows live in this page until
 * they are saved; the file itself never leaves the parse request.
 */
export function CrmImportPage() {
  const { me } = useMe()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const mode = parseMode(params.get('modus'))
  const history = params.get('tab') === 'historie'
  const [step, setStep] = useState<Step>('file')
  const [settings, setSettings] = useState<ImportSettings>(() => {
    // `?lijst=` (the link of "Importeren" on a list page) preselects the list; the name follows once the lists are loaded.
    const wanted = params.get('lijst')
    return wanted && isUuid(wanted) ? { ...emptySettings(), list: { id: wanted, name: '' } } : emptySettings()
  })
  const [file, setFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<ParsedFile | null>(null)
  const [mapping, setMapping] = useState<(CrmField | null)[]>([])
  const [mappedFor, setMappedFor] = useState<ParsedFile | null>(null)
  const [inputs, setInputs] = useState<RowInput[]>([])
  const [review, setReview] = useState<Review>(EMPTY_REVIEW)
  const [dirty, setDirty] = useState<ReadonlySet<number>>(new Set())
  const [checking, setChecking] = useState(0)
  const [committing, setCommitting] = useState(false)
  // How often each row was edited. A verdict that comes back for an older edit of its
  // row is dropped: an answer to a cell the user has changed again would be wrong.
  const edits = useRef(new Map<number, number>())
  // The newest check; only its answer may set the organisations panel.
  const latestCheck = useRef(0)
  const memory = useMemo(() => crmMappingMemory(mode), [mode])
  const fields = useMemo(() => fieldsFor(mode), [mode])

  if (me && me.role !== 'admin' && me.role !== 'team') {
    return <p className="vonk-card p-6 text-sm text-muted-foreground">Alleen het team kan relaties importeren.</p>
  }

  /** The mode decides the fields and the suggested mapping: a file already read is read again. */
  async function changeMode(next: ImportMode) {
    if (next === mode) return
    setParams(
      (current) => {
        const copy = new URLSearchParams(current)
        copy.set('modus', next)
        return copy
      },
      { replace: true },
    )
    setMappedFor(null)
    setMapping([])
    if (file && parsed) {
      try {
        setParsed(await parseImportFile(file, next, { sheet: parsed.sheet, headerRow: parsed.header_row }))
      } catch (caught) {
        toast.error(caught instanceof Error ? caught.message : 'Bestand inlezen mislukt.')
        setParsed(null)
      }
    }
  }

  function toMapping() {
    if (!parsed) return
    if (mappedFor !== parsed) {
      const remembered = memory.recall(parsed.headers)
      setMapping(remembered ?? parsed.suggested_mapping)
      setMappedFor(parsed)
    }
    setStep('mapping')
  }

  /**
   * Replace the server's verdicts. A row whose status changed gets its new default, and
   * so does a row whose chosen option is gone (its candidates changed).
   */
  function apply(rows: PreviewRow[]) {
    setReview((current) => {
      const nextRows = [...current.rows]
      const nextChoices = [...current.choices]
      for (const row of rows) {
        const chosen = nextChoices[row.index]
        if (nextRows[row.index]?.status !== row.status || !chosen || !isOffered(row, chosen)) {
          nextChoices[row.index] = defaultChoice(row)
        }
        nextRows[row.index] = row
      }
      return { ...current, rows: nextRows, choices: nextChoices }
    })
  }

  async function check(rows: RowInput[], only?: number[]): Promise<PreviewResult | null> {
    setChecking((count) => count + 1)
    const ticket = ++latestCheck.current
    try {
      const result = await previewImport({
        mode,
        settings: settingsPayload(settings, mode),
        rows,
        ...(only ? { only } : {}),
      })
      if (ticket === latestCheck.current) {
        setReview((current) => ({
          ...current,
          newOrganisations: result.new_organisations,
          existingOrganisations: result.existing_organisations,
        }))
      }
      return result
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Controleren mislukt.')
      return null
    } finally {
      setChecking((count) => count - 1)
    }
  }

  /** Check again (all rows, or `only` these) and show what came back for rows not edited since. */
  async function recheck(only?: number[], sent: RowInput[] = inputs) {
    const editedThen = sent.map((_row, index) => edits.current.get(index) ?? 0)
    const result = await check(sent, only)
    if (result) {
      apply(result.rows.filter((row) => (edits.current.get(row.index) ?? 0) === editedThen[row.index]))
    }
  }

  async function startPreview() {
    if (!parsed) return
    memory.remember(parsed.headers, mapping)
    const rows = applyMapping(parsed, mapping)
    const result = await check(rows)
    if (!result) return
    edits.current.clear()
    setInputs(rows)
    setReview({
      rows: result.rows,
      choices: result.rows.map(defaultChoice),
      newOrganisations: result.new_organisations,
      existingOrganisations: result.existing_organisations,
    })
    setDirty(new Set())
    setStep('preview')
  }

  function edit(index: number, field: RowField, value: string) {
    edits.current.set(index, (edits.current.get(index) ?? 0) + 1)
    setInputs((current) => current.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
    setDirty((current) => new Set(current).add(index))
  }

  function leave(index: number) {
    if (!dirty.has(index)) return
    setDirty((current) => {
      const next = new Set(current)
      next.delete(index)
      return next
    })
    // A row that repeats this one may no longer do so (another name, or an error now):
    // check those again with it, or the whole list when they are too many for one check.
    const only = [index, ...review.rows.filter((row) => row.duplicate_of === index).map((row) => row.index)]
    void recheck(only.length <= MAX_ONLY ? only : undefined)
  }

  /** Give these rows another organisation name (rename or merge) and check them again. */
  function renameOrganisation(indexes: number[], name: string) {
    for (const index of indexes) edits.current.set(index, (edits.current.get(index) ?? 0) + 1)
    const next = inputs.map((row, i) => (indexes.includes(i) ? { ...row, organisation: name } : row))
    setInputs(next)
    void recheck(indexes.length <= MAX_ONLY ? indexes : undefined, next)
  }

  function choose(index: number, choice: RowChoice) {
    setReview((current) => ({
      ...current,
      choices: current.choices.map((item, i) => (i === index ? choice : item)),
    }))
  }

  function skip(indexes: number[]) {
    setReview((current) => ({
      ...current,
      choices: current.choices.map((item, i) =>
        indexes.includes(i) && current.rows[i]?.actions.includes('skip') ? { ...item, action: 'skip' } : item,
      ),
    }))
  }

  /** Every question still open is answered "new": the row, and the look-alike organisation. */
  function allNew() {
    setReview((current) => ({
      ...current,
      choices: current.choices.map((item, i) => {
        const row = current.rows[i]
        if (!row) return item
        let next = item
        if (next.action === null && row.actions.includes('new')) next = { ...next, action: 'new' }
        if (row.organisation_choice_required && next.organisationChoice === null) {
          next = { ...next, ...parseOrganisationValue(ORGANISATION_NEW) }
        }
        return next
      }),
    }))
  }

  async function commit() {
    if (!parsed) return
    setCommitting(true)
    try {
      const batch = await commitImport({
        mode,
        filename: parsed.filename,
        file_sha256: parsed.file_sha256,
        sheet: parsed.sheet,
        header_row: parsed.header_row,
        mapping,
        settings: settingsPayload(settings, mode),
        rows: commitRows(mode, inputs, review.rows, review.choices),
      })
      navigate(`/relaties/importeren/${batch.id}`, { state: { batch } })
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Importeren mislukt.')
      setCommitting(false)
    }
  }

  const columns = columnsFor(mode, mappedFields(mapping))

  return (
    <DesktopOnly>
      <section className="flex flex-col gap-4">
        <PageHeader
          title="Relaties importeren"
          subtitle="Contacten of organisaties uit een Excel- of CSV-bestand in één keer inlezen."
        />
        <nav aria-label="Importeren" className="flex gap-2">
          {[
            { tab: null, label: 'Nieuwe import' },
            { tab: 'historie', label: 'Historie' },
          ].map((item) => {
            const active = item.tab === null ? !history : history
            return (
              <Link
                key={item.label}
                to={item.tab ? `?modus=${mode}&tab=${item.tab}` : `?modus=${mode}`}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'rounded-full px-3 py-1 text-sm font-semibold',
                  active ? 'bg-primary text-primary-foreground' : 'bg-white text-foreground',
                )}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>
        {history ? (
          <ImportHistory />
        ) : (
          <>
            <ImportStepper steps={STEPS} current={step} />
            {step === 'file' ? (
              <div className="flex flex-col gap-4">
                <fieldset className="vonk-card flex flex-col gap-2 p-4">
                  <legend className="sr-only">Wat importeer je?</legend>
                  <h2 className="text-base font-semibold text-foreground">Wat importeer je?</h2>
                  <div className="flex flex-wrap gap-4">
                    {MODES.map((option) => (
                      <label key={option.value} className="flex items-start gap-2 text-sm text-foreground">
                        <input
                          type="radio"
                          name="modus"
                          value={option.value}
                          checked={mode === option.value}
                          onChange={() => void changeMode(option.value)}
                          className="mt-1"
                        />
                        <span>
                          <span className="font-semibold">{option.label}</span>
                          <span className="block text-muted-foreground">{option.hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <FilePicker
                  file={file}
                  parsed={parsed}
                  onParsed={(nextFile, nextParsed) => {
                    setFile(nextFile)
                    setParsed(nextParsed)
                    setMappedFor(null)
                  }}
                  parse={(next, options) => parseImportFile(next, mode, options)}
                  downloadTemplate={() => downloadImportTemplate(mode)}
                  templateLabel={mode === 'contacten' ? 'Download sjabloon relaties' : 'Download sjabloon organisaties'}
                >
                  {({ reading }) => (
                    <SettingsStep
                      mode={mode}
                      settings={settings}
                      onSettings={setSettings}
                      canContinue={parsed !== null && !reading}
                      onNext={toMapping}
                    />
                  )}
                </FilePicker>
              </div>
            ) : null}
            {step === 'mapping' && parsed ? (
              <ColumnMapping
                parsed={parsed}
                mapping={mapping}
                fields={fields}
                problems={mappingProblems(mapping, mode)}
                onChange={setMapping}
                onBack={() => setStep('file')}
                onNext={() => void startPreview()}
                busy={checking > 0}
              />
            ) : null}
            {step === 'preview' ? (
              <PreviewGrid
                mode={mode}
                lead={columns.lead}
                others={columns.others}
                inputs={inputs}
                rows={review.rows}
                choices={review.choices}
                checking={checking > 0}
                committing={committing}
                listName={mode === 'contacten' ? settings.list?.name : undefined}
                panel={
                  mode === 'contacten' ? (
                    <OrganisationsPanel
                      newOrganisations={review.newOrganisations}
                      existingOrganisations={review.existingOrganisations}
                      onRename={renameOrganisation}
                    />
                  ) : null
                }
                onEdit={edit}
                onLeave={leave}
                onChoice={choose}
                onAllNew={allNew}
                onSkip={skip}
                onRecheckAll={() => void recheck()}
                onBack={() => setStep('mapping')}
                onCommit={() => void commit()}
              />
            ) : null}
          </>
        )}
      </section>
    </DesktopOnly>
  )
}
